import React from 'react';
import { Link } from 'react-router-dom';
import { Matter, SortState } from '../../types/legislation';

interface LegislationTableProps {
  items: Matter[];
  sort: SortState;
  onSort: (field: SortState['field']) => void;
}

const LegislationTable: React.FC<LegislationTableProps> = ({ items, sort, onSort }) => {
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('enacted') || statusLower.includes('adopted')) {
      return 'bg-green-100 text-green-800';
    }
    if (statusLower.includes('introduced')) {
      return 'bg-blue-100 text-blue-800';
    }
    if (statusLower.includes('committee')) {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (statusLower.includes('filed') || statusLower.includes('withdrawn')) {
      return 'bg-gray-100 text-gray-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const SortIcon: React.FC<{ field: SortState['field'] }> = ({ field }) => {
    if (sort.field !== field) {
      return <span className="ml-1 text-gray-400">↕</span>;
    }
    return <span className="ml-1">{sort.direction === 'asc' ? '↑' : '↓'}</span>;
  };

  const SortableHeader: React.FC<{
    field: SortState['field'];
    children: React.ReactNode;
    className?: string;
  }> = ({ field, children, className = '' }) => (
    <th
      onClick={() => onSort(field)}
      className={`px-4 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none ${className}`}
    >
      <span className="inline-flex items-center">
        {children}
        <SortIcon field={field} />
      </span>
    </th>
  );

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <SortableHeader field="MatterFile" className="w-32">
              File #
            </SortableHeader>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Title</th>
            <SortableHeader field="MatterTypeName" className="w-32">
              Type
            </SortableHeader>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 w-40">
              Sponsor
            </th>
            <SortableHeader field="MatterStatusName" className="w-32">
              Status
            </SortableHeader>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 w-40">
              Committee
            </th>
            <SortableHeader field="MatterIntroDate" className="w-32">
              Intro Date
            </SortableHeader>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {items.map((item) => (
            <tr key={item.MatterId} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-3 text-sm">
                <Link
                  to={`/legislation/${item.MatterId}`}
                  className="text-nyc-blue hover:underline font-medium"
                >
                  {item.MatterFile}
                </Link>
              </td>
              <td className="px-4 py-3 text-sm text-gray-900">
                <Link to={`/legislation/${item.MatterId}`} className="hover:text-nyc-blue">
                  <div className="line-clamp-2">{item.MatterTitle || item.MatterName}</div>
                </Link>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{item.MatterTypeName}</td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {item.prime_sponsor_name || '-'}
              </td>
              <td className="px-4 py-3 text-sm">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                    item.MatterStatusName
                  )}`}
                >
                  {item.MatterStatusName}
                </span>
              </td>
              <td className="px-4 py-3 text-sm">
                {item.MatterBodyName && (
                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-800">
                    {item.MatterBodyName}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {formatDate(item.MatterIntroDate)}
                {item.matched_attachment_text === 1 && (
                  <span className="ml-2 inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800">
                    doc match
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default LegislationTable;
